import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PagoConfirmacion() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [message, setMessage] = useState('Confirmando tu pago con PayPhone...');
  const [details, setDetails] = useState(null);
  const [diagnostico, setDiagnostico] = useState(null);
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const id = searchParams.get('id');
    const clientTransactionId = searchParams.get('clientTransactionId') ||
                                searchParams.get('clientTxId') ||
                                sessionStorage.getItem('payphone_pending_tx');

    // === DIAGNÓSTICO PAYPHONE (visible en consola del navegador) ===
    console.log('%c[PAYPHONE DIAGNÓSTICO] URL de retorno recibida', 'color:#fff;background:#0070ba;padding:2px 6px;border-radius:4px');
    console.log('[PAYPHONE] URL completa:', window.location.href);
    console.log('[PAYPHONE] id (PayPhone):', id);
    console.log('[PAYPHONE] clientTransactionId:', clientTransactionId);
    console.log('[PAYPHONE] todos los parámetros URL:', Object.fromEntries([...searchParams.entries()]));

    if (!id || !clientTransactionId) {
      console.error('[PAYPHONE] FALTAN datos en la URL de retorno. id o clientTransactionId vacíos.');
      setStatus('error');
      setMessage('Faltan datos de la transacción en la URL de retorno.');
      setDiagnostico({ error: 'Faltan id o clientTransactionId en la URL', url: window.location.href });
      return;
    }

    (async () => {
      const reqBody = { id: parseInt(id, 10), clientTransactionId };
      console.log('[PAYPHONE] Enviando confirmación al backend:', `${API}/payphone/confirm`, reqBody);
      try {
        const res = await axios.post(`${API}/payphone/confirm`, reqBody);

        console.log('%c[PAYPHONE] RESPUESTA del backend (confirm)', 'color:#fff;background:#16a34a;padding:2px 6px;border-radius:4px');
        console.log('[PAYPHONE] status:', res.data.status);
        console.log('[PAYPHONE] delay_segundos (tiempo desde compra hasta confirm):', res.data.delay_segundos);
        console.log('[PAYPHONE] intentos a PayPhone (cada respuesta real):', res.data.intentos);
        console.log('[PAYPHONE] respuesta_final de PayPhone:', res.data.data);
        console.log('[PAYPHONE] objeto completo:', res.data);

        setDiagnostico(res.data);

        if (res.data.status === 'success') {
          setStatus('success');
          setMessage('¡Pago confirmado exitosamente!');
          setDetails(res.data.data || null);
          sessionStorage.removeItem('payphone_pending_tx');
          const montoUsd = res.data.data?.amount ? (res.data.data.amount / 100).toFixed(2) : '';
          const graciasUrl = montoUsd ? `/gracias?value=${montoUsd}&currency=USD` : '/gracias';
          setTimeout(() => navigate(graciasUrl), 2500);
        } else {
          setStatus('error');
          setMessage(res.data.message || 'El pago no fue aprobado por PayPhone.');
          setDetails(res.data.data || null);
        }
      } catch (err) {
        console.error('%c[PAYPHONE] ERROR al confirmar el pago', 'color:#fff;background:#dc2626;padding:2px 6px;border-radius:4px');
        console.error('[PAYPHONE] error completo:', err);
        console.error('[PAYPHONE] respuesta del servidor:', err.response?.data);
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Error al confirmar el pago con PayPhone.');
        setDiagnostico({ error: true, http_status: err.response?.status, respuesta: err.response?.data });
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6" data-testid="pago-confirmacion-page">
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-10 border border-slate-100 dark:border-white/5 text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-[#0070ba] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-black uppercase tracking-tight mb-3" data-testid="pago-status-title">Procesando pago</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-950/40 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-black uppercase italic tracking-tighter mb-3 text-emerald-600" data-testid="pago-status-title">¡Pago exitoso!</h1>
            <p className="text-sm text-muted-foreground mb-6">{message}</p>
            {details && (
              <div className="bg-slate-50 dark:bg-white/5 rounded-xl p-4 text-left text-xs space-y-1 mb-6">
                {details.authorizationCode && <p><strong>Autorización:</strong> {details.authorizationCode}</p>}
                {details.transactionId && <p><strong>ID Transacción:</strong> {details.transactionId}</p>}
                {details.amount && <p><strong>Monto:</strong> ${(details.amount / 100).toFixed(2)}</p>}
                {details.cardBrand && <p><strong>Tarjeta:</strong> {details.cardBrand} •••• {details.lastDigits}</p>}
              </div>
            )}
            <button
              onClick={() => navigate('/usuario?tab=boletos')}
              className="w-full h-12 bg-[#0070ba] hover:bg-[#003087] text-white font-black uppercase text-xs tracking-widest rounded-xl flex items-center justify-center gap-2 transition-colors"
              data-testid="ver-boletos-btn"
            >
              Ver mis boletos <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[10px] text-muted-foreground mt-3 italic">Redirigiendo automáticamente...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 dark:bg-red-950/40 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-black uppercase italic tracking-tighter mb-3 text-red-600" data-testid="pago-status-title">Pago no completado</h1>
            <p className="text-sm text-muted-foreground mb-6">{message}</p>
            {details && (details.errorCode || details.message) && (
              <div className="bg-red-50 dark:bg-red-950/20 rounded-xl p-4 text-left text-xs space-y-1 mb-6 border border-red-100 dark:border-red-900/30" data-testid="pago-error-details">
                {details.message && <p><strong>Detalle PayPhone:</strong> {details.message}</p>}
                {details.errorCode != null && <p><strong>Código de error:</strong> {details.errorCode}</p>}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex-1 h-12 border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 font-black uppercase text-xs tracking-widest rounded-xl transition-colors"
                data-testid="volver-btn"
              >
                Reintentar
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-200 text-white dark:text-slate-900 font-black uppercase text-xs tracking-widest rounded-xl transition-colors"
                data-testid="ir-inicio-btn"
              >
                Ir al inicio
              </button>
            </div>
          </>
        )}
        {diagnostico && (
          <div className="mt-6 text-left" data-testid="payphone-diagnostico">
            <details open>
              <summary className="cursor-pointer text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                Diagnóstico PayPhone (cópialo si el pago falla)
              </summary>
              <button
                onClick={() => { navigator.clipboard.writeText(JSON.stringify(diagnostico, null, 2)); }}
                className="mb-2 text-[10px] px-3 py-1 rounded-lg bg-slate-900 text-white font-bold uppercase tracking-widest"
                data-testid="copiar-diagnostico-btn"
              >
                Copiar diagnóstico
              </button>
              <pre className="bg-slate-900 text-emerald-300 text-[10px] leading-relaxed p-3 rounded-xl overflow-x-auto max-h-72 overflow-y-auto whitespace-pre-wrap break-all">
{JSON.stringify(diagnostico, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
